import { Controller } from "@hotwired/stimulus"

// Replaces native time/datetime-local inputs with a Chrome-style
// three-column scrollable picker (hours 01–12, minutes 00–59, AM/PM).
//
// Usage: data-controller="time-select" on the .datetime-field-wrapper div.
// Optional: data-time-select-utc-offset-minutes-value="<integer>" to anchor
//   "now" and "today" to the location's timezone rather than the browser's.

export default class extends Controller {
  static values = { utcOffsetMinutes: Number }

  // Returns a Date representing "now" at the location's timezone offset,
  // or the browser's local now if no offset is configured.
  // hasUtcOffsetMinutesValue is true only when the data attribute is present in the DOM.
  #locationNow() {
    if (!this.hasUtcOffsetMinutesValue) return new Date()
    const utcMs = Date.now() + new Date().getTimezoneOffset() * 60000 // shift to UTC ms
    return new Date(utcMs + this.utcOffsetMinutesValue * 60000)
  }

  connect() {
    const timeInput = this.element.querySelector('input[type="time"]')
    const dtInput   = this.element.querySelector('input[type="datetime-local"]')
    this._input = timeInput || dtInput
    this._isDT  = !!dtInput

    if (!this._input) return // no time input found — bail safely

    const { hour12, minute, isPm, datePart } = this.#parse(this._input.value)
    this._h = hour12; this._m = minute; this._ap = isPm ? "PM" : (isPm === false ? "AM" : null)
    this._date = datePart || ""

    const wasRequired = this._input.hasAttribute("required")
    this._input.style.display = "none"
    this._input.removeAttribute("required") // prevent "not focusable" browser error on hidden required inputs
    this._input.removeAttribute("max")      // same issue: max on a hidden input causes an unfocusable validation error
    this.#buildUI()

    // If the original input was required, insert a focusable proxy <input required>
    // so the browser can enforce required validation (and show its tooltip near this field).
    // NOTE: pointer-events must NOT be "none" — Chrome only renders "Please fill out this
    // field" tooltips on inputs that are pointer-event-enabled.
    if (wasRequired) {
      this._proxy = document.createElement("input")
      this._proxy.type = "text"
      this._proxy.setAttribute("required", "")
      this._proxy.setAttribute("aria-label", "Incident date and time (required)")
      this._proxy.tabIndex = -1
      // Invisible but occupies space inside the wrapper so the browser tooltip appears here.
      // pointer-events left at "auto" (default) so Chrome can show its native required tooltip.
      this._proxy.style.cssText = "position:absolute;opacity:0;width:1px;height:100%;top:0;left:0;border:0;padding:0;"
      // Pre-fill with any value Rails already put in the native input (re-render after server error)
      this._proxy.value = this._input.value
      this.element.style.position = "relative"
      this.element.appendChild(this._proxy)
    }

    this._outsideClick = (e) => { if (!this.element.contains(e.target)) this.#close() }
    document.addEventListener("mousedown", this._outsideClick)
  }

  disconnect() { document.removeEventListener("mousedown", this._outsideClick) }

  // ── Build ──────────────────────────────────────────────────────────────────

  #buildUI() {
    // Display row (looks like a native time input)
    this._display = document.createElement("div")
    this._display.className = "tp-display"
    this._display.addEventListener("click", (e) => { e.stopPropagation(); this.#toggle() })

    // For datetime-local, add a date input before the time display
    if (this._isDT) {
      // Compute today's date string anchored to the location's timezone (falls back to browser local).
      const now = this.#locationNow()
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`

      this._dateInput = document.createElement("input")
      this._dateInput.type = "date"
      this._dateInput.className = "tp-date-part"
      this._dateInput.value = this._date
      this._dateInput.min = "2010-01-01"
      this._dateInput.max = todayStr  // greys out future dates in the calendar picker
      this._dateInput.addEventListener("change", () => {
        // Clamp to today if the user manually typed a future date
        if (this._dateInput.value > todayStr) this._dateInput.value = todayStr
        this._date = this._dateInput.value
        this.#sync()
      })
      this._input.before(this._dateInput)

      const sep = document.createElement("span")
      sep.className = "tp-separator"
      sep.textContent = "at"
      this._input.before(sep)
    }

    this.#refreshDisplay()
    this._input.before(this._display)

    // Dropdown panel with 3 columns
    this._dropdown = document.createElement("div")
    this._dropdown.className = "tp-dropdown"
    this._dropdown.style.display = "none"

    const hours = []; for (let h = 1; h <= 12; h++) hours.push(String(h).padStart(2, "0"))
    const mins  = []; for (let m = 0; m <= 59; m++) mins.push(String(m).padStart(2, "0"))

    this._hCol  = this.#col(hours, this._h ? String(this._h).padStart(2, "0") : null, v => { this._h = parseInt(v); this.#sync() })
    this._mCol  = this.#col(mins, this._m !== null ? String(this._m).padStart(2, "0") : null, v => { this._m = parseInt(v); this.#sync() })
    this._apCol = this.#col(["AM", "PM"], this._ap, v => { this._ap = v; this.#sync() })

    this._dropdown.append(this._hCol, this._mCol, this._apCol)
    this._input.before(this._dropdown)
  }

  #col(items, selected, onSelect) {
    const col = document.createElement("div")
    col.className = "tp-column"
    items.forEach(val => {
      const item = document.createElement("div")
      item.className = "tp-item" + (val === selected ? " tp-selected" : "")
      item.textContent = val
      item.addEventListener("click", (e) => {
        e.stopPropagation()
        col.querySelectorAll(".tp-item").forEach(i => i.classList.remove("tp-selected"))
        item.classList.add("tp-selected")
        onSelect(val)
        this.#refreshDisplay()
      })
      col.append(item)
    })
    requestAnimationFrame(() => {
      const sel = col.querySelector(".tp-selected")
      if (sel) sel.scrollIntoView({ block: "center", behavior: "auto" })
    })
    return col
  }

  // ── Display ────────────────────────────────────────────────────────────────

  #refreshDisplay() {
    const h  = this._h  !== null ? String(this._h).padStart(2, "0")  : "--"
    const m  = this._m  !== null ? String(this._m).padStart(2, "0")  : "--"
    const ap = this._ap || "--"
    this._display.innerHTML =
      `<span class="tp-seg">${h}</span>` +
      `<span class="tp-colon">:</span>` +
      `<span class="tp-seg">${m}</span>` +
      `<span class="tp-seg tp-ampm">${ap}</span>`
  }

  #toggle() {
    const open = this._dropdown.style.display === "none"
    this._dropdown.style.display = open ? "flex" : "none"
    if (open) {
      this._dropdown.querySelectorAll(".tp-column").forEach(col => {
        const sel = col.querySelector(".tp-selected")
        if (sel) sel.scrollIntoView({ block: "center", behavior: "auto" })
      })
    }
  }

  #close() { if (this._dropdown) this._dropdown.style.display = "none" }

  // ── Sync to hidden input ───────────────────────────────────────────────────

  #sync() {
    if (this._h === null || this._m === null || !this._ap) {
      this._input.value = ""
      if (this._proxy) this._proxy.value = ""
      return
    }
    let h24 = this._h
    if (this._ap === "PM" && h24 !== 12) h24 += 12
    if (this._ap === "AM" && h24 === 12) h24 = 0
    const time = `${String(h24).padStart(2, "0")}:${String(this._m).padStart(2, "0")}`
    this._input.value = this._isDT ? (this._date ? `${this._date}T${time}` : "") : time
    if (this._proxy) {
      this._proxy.value = this._input.value
      // Client-side future-datetime guard: set a custom validity message so the
      // browser blocks submission and shows a tooltip — catches future times on
      // today's date that the date input max alone cannot prevent.
      // Compare the submitted local datetime string against "now at location" so that
      // an EST user at midnight is not blocked because the server default is Pacific time.
      if (this._isDT && this._input.value) {
        const submittedMs = new Date(this._input.value).getTime() // treat as local naive
        // Shift "now" into the location's timezone frame for an apples-to-apples comparison.
        const locationNow = this.#locationNow()
        // Build a naive datetime from locationNow using the same YYYY-MM-DDTHH:MM pattern
        const pad = n => String(n).padStart(2, "0")
        const locationNowStr = `${locationNow.getFullYear()}-${pad(locationNow.getMonth()+1)}-${pad(locationNow.getDate())}T${pad(locationNow.getHours())}:${pad(locationNow.getMinutes())}`
        const locationNowMs = new Date(locationNowStr).getTime()
        if (submittedMs > locationNowMs) {
          this._proxy.setCustomValidity("Incident date and time cannot be in the future")
        } else {
          this._proxy.setCustomValidity("")
        }
      } else {
        this._proxy.setCustomValidity("")
      }
    }
    this.#refreshDisplay()
  }

  // ── Parse ──────────────────────────────────────────────────────────────────

  #parse(val) {
    if (!val) return { hour12: null, minute: null, isPm: null, datePart: null }
    let timePart = val, datePart = null
    if (val.includes("T")) { [datePart, timePart] = val.split("T") }
    // split on ":" and take only h/m — safely ignores seconds or fractional seconds
    const parts = timePart.split(":")
    const h = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    if (isNaN(h) || isNaN(m)) return { hour12: null, minute: null, isPm: null, datePart }
    return { hour12: h % 12 || 12, minute: m, isPm: h >= 12, datePart }
  }
}

